/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { devices, fontFamilies } from "components/shared/Styles";
import * as React from "react";

interface ActionProps {
  text: string;
  url: string;
}
interface FeatureProps {
  image: ResponsiveImageOutput;
  title: string;
  body: string;
  action?: ActionProps;
}

export const Feature: React.FC<FeatureProps> = (props) => {
  return (
    <FeatureContainer>
      <img
        style={{ maxWidth: 554 }}
        srcSet={props.image.srcSet}
        alt=""
        loading="lazy"
      />
      <FeatureDescription>
        <FeatureTitle>{props.title}</FeatureTitle>
        <FeatureBody>{props.body}</FeatureBody>
        {props.action && (
          <FeatureAction>
            <sp-button
              size="l"
              variant="primary"
              href={props.action.url}
              target="_blank"
            >
              {props.action.text}
            </sp-button>
          </FeatureAction>
        )}
      </FeatureDescription>
    </FeatureContainer>
  );
};

const FeatureContainer = styled.div`
  display: flex;
  flex-direction: row;

  ${devices.large} {
    &:nth-child(odd) {
      flex-direction: row-reverse;
    }
  }

  justify-content: space-between;
  align-items: center;
  margin-top: 72px;
  margin-bottom: 72px;

  gap: 48px;

  img {
    border-radius: 32px;
    width: 50%;
    object-fit: cover;
    align-self: stretch;
  }

  ${devices.small} {
    flex-direction: column;
    padding: 0 24px;
    gap: 24px;

    img {
      width: 100%;
      max-height: 324px;
      object-fit: cover;
    }
  }
`;

const FeatureDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${devices.large} {
    width: 50%;
  }
`;

const FeatureTitle = styled.div`
  font: normal normal 900 45px/52px ${fontFamilies};
  color: var(--spectrum-global-color-static-black);

  ${devices.small} {
    font: normal normal 900 34px/40px ${fontFamilies};
  }
`;

const FeatureAction = styled.div`
  margin-top: 16px;
`;

const FeatureBody = styled.div`
  font: normal normal normal 22px/27px ${fontFamilies};
  color: var(--spectrum-gray-900);
`;
